import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { branchOfficeService } from '../services/branchOfficeService';
import type { BranchOffice } from '../types/branchOffice.types';

export function useBranchOffices(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  activeOnly?: boolean;
}) {
  return useQuery({
    queryKey: ['branchOffices', params],
    queryFn: () => branchOfficeService.getBranchOffices(params),
    staleTime: 0, // Always refetch to get fresh data
    gcTime: 5 * 60 * 1000, // 5 minutes cache time
  });
}

export function useBranchOffice(id: string) {
  return useQuery({
    queryKey: ['branchOffice', id],
    queryFn: () => branchOfficeService.getBranchOfficeById(id),
    enabled: !!id,
  });
}

export function useCreateBranchOffice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (branch: Partial<BranchOffice>) => branchOfficeService.createBranchOffice(branch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branchOffices'] });
      queryClient.invalidateQueries({ queryKey: ['consumption'] });
    },
  });
}

export function useUpdateBranchOffice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<BranchOffice> }) =>
      branchOfficeService.updateBranchOffice(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branchOffices'] });
    },
  });
}

export function useDeleteBranchOffice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, companyId }: { id: string; companyId: string }) =>
      branchOfficeService.deleteBranchOffice(id, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branchOffices'] });
      queryClient.invalidateQueries({ queryKey: ['consumption'] });
    },
  });
}

export function useReactivateBranchOffice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, companyId }: { id: string; companyId: string }) =>
      branchOfficeService.reactivateBranchOffice(id, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branchOffices'] });
      queryClient.invalidateQueries({ queryKey: ['consumption'] });
    },
  });
}
